import React, { useEffect, useRef, useState } from "react";
import { R6IonInput } from "./r6ionic/R6IonInput";
import { IonItem } from "@ionic/react";
import { RegisterOptions, useWatch } from "react-hook-form";
import { errorProps } from "../services/errorService";
import { UseListRenderer, UseListSearch } from "./hooks/autocomplete/types";
import { useIsLanguageJapanese } from "./hooks";
import { useSafeKeyboard } from "./hooks/useSafeKeyboard";

interface AutocompleteInputProps {
    label: string;
    name: string;
    errors: any;
    ready: boolean;
    setValue: (name: any, value: any, options?: any) => void;
    testId: string;
    searchFn: UseListSearch<any>;
    renderFn: UseListRenderer<any>;
    rules?: RegisterOptions<any>;
    onFocus?: () => void;
    onBlur?: () => void;
    placeholder?: string;
    useFlex?: boolean;
    disabled?: boolean;
}

function AutocompleteInput({
    label,
    name,
    errors,
    ready,
    setValue,
    testId,
    searchFn,
    renderFn,
    onFocus,
    onBlur,
    rules,
    placeholder,
    useFlex,
    disabled = false,
}: AutocompleteInputProps) {
    const MIN_SEARCH_CHARS = 3;
    const [isFocussed, setIsFocussed] = useState<boolean>(false);
    const [suggestions, setSuggestions] = React.useState<any>(null);
    const value = useWatch({ name: name });
    const inputRef = useRef<HTMLIonItemElement>(null);
    const { isOpen: isKeyboardVisible } = useSafeKeyboard();

    useEffect(() => {
        if (!ready) {
            return;
        }

        const res =
            value && value.length >= MIN_SEARCH_CHARS
                ? searchFn(value)
                : searchFn("");
        setSuggestions(res);
    }, [searchFn, ready, value]);

    const scrollToInput = () => {
        if (!inputRef.current) return;
        const ionContent = inputRef.current.closest("ion-content") as HTMLIonContentElement | null;
        if (!ionContent) return;

        ionContent.getScrollElement().then((scrollEl) => {
            if (!inputRef.current) return;
            const inputRect = inputRef.current.getBoundingClientRect();
            const contentRect = ionContent.getBoundingClientRect();
            const offsetFromContentTop = inputRect.top - contentRect.top;
            // Small padding so input isn't flush against the header
            const newScrollTop = scrollEl.scrollTop + offsetFromContentTop - 8;
            ionContent.scrollToPoint(0, newScrollTop, 300);
        });
    };

    useEffect(() => {
        if (isFocussed && isKeyboardVisible && inputRef.current) {
            scrollToInput();
        }
    }, [isFocussed, isKeyboardVisible]);

    return (
        <>
            <IonItem
                ref={inputRef}
                data-testid={testId}
                {...errorProps(name, errors)}
                lines="full"
            >
                <R6IonInput
                    disabled={disabled}
                    name={name}
                    rules={rules}
                    onFocus={() => {
                        setIsFocussed(true);
                        onFocus && onFocus();
                    }}
                    onBlur={() => {
                        setIsFocussed(false);
                        onBlur && onBlur();
                    }}
                    placeholder={placeholder}
                    debounce={500}
                    className={"align-text-end"}
                    label={label}
                />
            </IonItem>
            {/* onPointerDown preventDefault stops the input losing focus when tapping a suggestion */}
            <div
                onPointerDown={(e) => e.preventDefault()}
                className={
                    !suggestions ||
                    !isFocussed ||
                    (value &&
                        value.length >= 3 &&
                        Object.keys(suggestions).length === 0)
                        ? "autocomplete"
                        : useFlex
                        ? "autocomplete open flex"
                        : "autocomplete open"
                }
            >
                {suggestions &&
                    renderFn(suggestions, (item) => {
                        setValue(name, item, { shouldValidate: true });
                        setIsFocussed(false);
                    })}
            </div>
        </>
    );
}

function SimpleInput({
    label,
    name,
    errors,
    testId,
    rules,
}: AutocompleteInputProps) {
    return (
        <IonItem
            data-testid={testId}
            {...errorProps(name, errors)}
            lines="full"
            onClick={(e) => {
                // scroll segment into view on click
                if (e.target) {
                    const el = e.target as HTMLElement;
                    const ionContent = el.closest("ion-content") as HTMLIonContentElement | null;
                    if (ionContent) {
                        ionContent.getScrollElement().then((scrollEl) => {
                            const elRect = el.getBoundingClientRect();
                            const contentRect = ionContent.getBoundingClientRect();
                            const offsetFromContentTop = elRect.top - contentRect.top;
                            const newScrollTop = scrollEl.scrollTop + offsetFromContentTop;
                            ionContent.scrollToPoint(0, newScrollTop, 300);
                        });
                    }
                }
            }}
        >
            <R6IonInput
                name={name}
                rules={rules}
                debounce={500}
                className={"align-text-end"}
                label={label}
            />
        </IonItem>
    );
}

const AutocompleteSwitch = (props: AutocompleteInputProps) => {
    const isLocalJP = useIsLanguageJapanese();
    if (isLocalJP) {
        return <SimpleInput {...props} />;
    }
    return <AutocompleteInput {...props} />;
};
export default AutocompleteSwitch;
